import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// src/components/UserAnalyticsPanel.tsx
// User analytics panel with US map and connection statistics
import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Circle, Tooltip } from 'react-leaflet';
import { Icon } from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './UserAnalyticsPanel.css';
import io from 'socket.io-client';
// Fix Leaflet default marker icon issue
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';
const DefaultIcon = new Icon({
    iconUrl: icon,
    shadowUrl: iconShadow,
    iconSize: [25, 41],
    iconAnchor: [12, 41]
});
// Helper to format session duration
function formatDuration(joinedAt) {
    const now = new Date();
    const joined = new Date(joinedAt);
    const diff = now.getTime() - joined.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);
    if (hours > 0) {
        return `${hours}h ${minutes}m`;
    }
    else if (minutes > 0) {
        return `${minutes}m ${seconds}s`;
    }
    else {
        return `${seconds}s`;
    }
}
// Helper to format bytes transferred
function formatBytes(bytes) {
    if (!bytes)
        return '0 B';
    const kb = bytes / 1024;
    const mb = kb / 1024;
    const gb = mb / 1024;
    if (gb >= 1)
        return `${gb.toFixed(1)} GB`;
    if (mb >= 1)
        return `${mb.toFixed(1)} MB`;
    if (kb >= 1)
        return `${kb.toFixed(1)} KB`;
    return `${bytes} B`;
}
// Helper to format milliseconds to readable time
function formatMs(ms) {
    const hours = Math.floor(ms / (1000 * 60 * 60));
    const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((ms % (1000 * 60)) / 1000);
    if (hours > 0) {
        return `${hours}h ${minutes}m`;
    }
    else if (minutes > 0) {
        return `${minutes}m ${seconds}s`;
    }
    else {
        return `${seconds}s`;
    }
}
// Helper to extract OS from platform/userAgent
function getOS(platform, userAgent) {
    if (!platform && !userAgent)
        return 'Unknown';
    const ua = userAgent || '';
    const plat = platform || '';
    if (ua.includes('Windows NT 10.0'))
        return 'Windows 11';
    if (ua.includes('Windows NT') || plat.includes('Win'))
        return 'Windows';
    if (ua.includes('Mac OS X') || plat.includes('Mac')) {
        const match = ua.match(/Mac OS X ([\d_]+)/);
        if (match) {
            const version = match[1].split('_')[0];
            return `macOS ${version}`;
        }
        return 'macOS';
    }
    if (ua.includes('iPhone') || ua.includes('iPad')) {
        const match = ua.match(/OS ([\d_]+)/);
        if (match) {
            const version = match[1].split('_').slice(0, 2).join('.');
            return `iOS ${version}`;
        }
        return 'iOS';
    }
    if (ua.includes('Android')) {
        const match = ua.match(/Android ([\d.]+)/);
        if (match) {
            return `Android ${match[1]}`;
        }
        return 'Android';
    }
    if (plat.includes('Linux'))
        return 'Linux';
    return plat || 'Unknown';
}
// Helper to extract GPU from WebGL fingerprint
function getGPU(webgl) {
    if (!webgl)
        return 'Unknown';
    console.log('ðŸŽ® GPU detection - WebGL string:', webgl);
    // WebGL string typically contains renderer info
    if (webgl.includes('NVIDIA')) {
        const match = webgl.match(/NVIDIA\s+([^\s,]+(?:\s+[^\s,]+)?)/i);
        if (match)
            return `NVIDIA ${match[1]}`;
        return 'NVIDIA GPU';
    }
    if (webgl.includes('AMD') || webgl.includes('Radeon')) {
        const match = webgl.match(/(RX\s+\d+\s+\w+|Radeon\s+[^\s,]+)/i);
        if (match)
            return `AMD ${match[1]}`;
        return 'AMD GPU';
    }
    if (webgl.includes('Apple')) {
        // Better Apple GPU detection for M-series chips
        if (webgl.includes('M3'))
            return 'Apple M3 GPU';
        if (webgl.includes('M2')) {
            if (webgl.includes('Max'))
                return 'Apple M2 Max';
            if (webgl.includes('Pro'))
                return 'Apple M2 Pro';
            if (webgl.includes('Ultra'))
                return 'Apple M2 Ultra';
            return 'Apple M2';
        }
        if (webgl.includes('M1')) {
            if (webgl.includes('Max'))
                return 'Apple M1 Max';
            if (webgl.includes('Pro'))
                return 'Apple M1 Pro';
            if (webgl.includes('Ultra'))
                return 'Apple M1 Ultra';
            return 'Apple M1';
        }
        return 'Apple GPU';
    }
    if (webgl.includes('Intel')) {
        const match = webgl.match(/Intel\(R\)\s+([^\s,]+(?:\s+[^\s,]+)?)/i);
        if (match)
            return `Intel ${match[1]}`;
        return 'Intel GPU';
    }
    if (webgl.includes('Adreno')) {
        const match = webgl.match(/Adreno\s+\(TM\)\s+(\d+)/i);
        if (match)
            return `Adreno ${match[1]}`;
        return 'Adreno GPU';
    }
    // Return first 30 characters as fallback
    return webgl.substring(0, 30);
}
// Helper to get device type from user agent
function getDeviceType(userAgent, touchSupport) {
    if (!userAgent)
        return 'Unknown';
    if (userAgent.includes('Mobile') || userAgent.includes('Android') ||
        userAgent.includes('iPhone') || userAgent.includes('iPod')) {
        return 'Mobile';
    }
    if (userAgent.includes('Tablet') || userAgent.includes('iPad')) {
        return 'Tablet';
    }
    return 'Desktop';
}
// Helper to shorten fingerprint ID
function shortenFingerprint(id) {
    if (!id)
        return 'N/A';
    return id.substring(0, 8).toUpperCase();
}
export default function UserAnalyticsPanel() {
    const [socket, setSocket] = useState(null);
    const [analyticsData, setAnalyticsData] = useState(null);
    const [connected, setConnected] = useState(false);
    const [error, setError] = useState(null);
    const [lastUpdate, setLastUpdate] = useState(new Date());
    // US center coordinates
    const US_CENTER = [39.8283, -98.5795];
    // GridChat server location (Piscataway, NJ)
    const SERVER_LOCATION = [40.5511, -74.4606];
    // Get roomId from URL
    const roomId = new URLSearchParams(window.location.search).get('roomId') || 'default';
    // Initialize socket connection
    useEffect(() => {
        console.log('ðŸ”Œ Connecting to GridChat server for analytics...');
        console.log('ðŸ“Š RoomId from URL:', roomId);
        const newSocket = io({
            transports: ['websocket', 'polling']
        });
        newSocket.on('connect', () => {
            console.log('âœ… Analytics socket connected');
            setConnected(true);
            setError(null);
            // Request initial analytics data with roomId
            newSocket.emit('get-analytics', { roomId });
        });
        newSocket.on('disconnect', () => {
            console.log('ðŸ”Œ Analytics socket disconnected');
            setConnected(false);
        });
        newSocket.on('analytics-data', (data) => {
            console.log('ðŸ“Š Received analytics data:', data);
            setAnalyticsData(data);
            setLastUpdate(new Date());
        });
        newSocket.on('analytics-error', (data) => {
            console.error('âŒ Analytics error:', data.error);
            setError(data.error);
        });
        setSocket(newSocket);
        // Cleanup on unmount
        return () => {
            newSocket.disconnect();
        };
    }, []);
    // Auto-refresh analytics every 5 seconds
    useEffect(() => {
        if (!socket || !connected)
            return;
        const interval = setInterval(() => {
            socket.emit('get-analytics', { roomId });
        }, 5000);
        return () => clearInterval(interval);
    }, [socket, connected, roomId]);
    // Calculate summary statistics
    const totalUsers = analyticsData?.users.length || 0;
    const usersWithLocation = analyticsData?.users.filter(u => u.location).length || 0;
    const avgLatency = totalUsers > 0
        ? Math.round(analyticsData.users.reduce((sum, u) => sum + (u.connectionStats?.latency || 0), 0) / totalUsers)
        : 0;
    const avgPacketLoss = totalUsers > 0
        ? (analyticsData.users.reduce((sum, u) => sum + (u.connectionStats?.packetLoss || 0), 0) / totalUsers).toFixed(1)
        : '0.0';
    if (error) {
        return (_jsxs("div", { className: "analytics-panel", children: [_jsxs("div", { className: "analytics-header", children: [_jsx("h1", { children: "\u00F0\u0178\u201C\u0160 User Analytics" }), _jsx("div", { className: "connection-status error", children: "\u00E2\u009D\u0152 Error" })] }), _jsx("div", { className: "analytics-error-message", children: _jsx("p", { children: error }) })] }));
    }
    return (_jsxs("div", { className: "analytics-panel", children: [_jsxs("div", { className: "analytics-header", children: [_jsx("h1", { children: "\u00F0\u0178\u201C\u0160 User Analytics" }), _jsxs("div", { className: "header-controls", children: [_jsx("div", { className: `connection-status ${connected ? 'connected' : 'disconnected'}`, children: connected ? 'ðŸŸ¢ Connected' : 'ðŸ”´ Disconnected' }), _jsxs("div", { className: "last-update", children: ["Updated: ", lastUpdate.toLocaleTimeString()] })] })] }), _jsxs("div", { className: "summary-stats", children: [_jsxs("div", { className: "stat-card", children: [_jsx("div", { className: "stat-label", children: "Total Users:" }), _jsx("div", { className: "stat-value", children: totalUsers })] }), _jsxs("div", { className: "stat-card", children: [_jsx("div", { className: "stat-label", children: "Geolocated:" }), _jsx("div", { className: "stat-value", children: usersWithLocation })] }), _jsxs("div", { className: "stat-card", children: [_jsx("div", { className: "stat-label", children: "Avg Latency:" }), _jsxs("div", { className: "stat-value", children: [avgLatency, "ms"] })] }), _jsxs("div", { className: "stat-card", children: [_jsx("div", { className: "stat-label", children: "Avg Packet Loss:" }), _jsxs("div", { className: "stat-value", children: [avgPacketLoss, "%"] })] })] }), _jsxs("div", { className: "analytics-content", children: [_jsxs("div", { className: "map-section", children: [_jsx("h2", { children: "\u00F0\u0178\u2014\u00BA\u00EF\u00B8\u008F User Locations (United States)" }), _jsx("div", { className: "map-container", children: _jsxs(MapContainer, { center: US_CENTER, zoom: 4, style: { height: '100%', width: '100%' }, scrollWheelZoom: true, children: [_jsx(TileLayer, { attribution: '\u00A9 <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors', url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" }), _jsx(Circle, { center: SERVER_LOCATION, radius: 20000, pathOptions: {
                                                color: '#ef4444',
                                                fillColor: '#ef4444',
                                                fillOpacity: 0.5,
                                                weight: 3
                                            }, children: _jsx(Tooltip, { permanent: true, direction: "top", offset: [0, -10], children: _jsx("div", { style: { textAlign: 'center', fontWeight: 'bold', color: '#ef4444' }, children: "\u00F0\u0178\u2013\u00A5\u00EF\u00B8\u008F GridChat Server" }) }) }), analyticsData?.users.map(user => {
                                            if (!user.location)
                                                return null;
                                            const position = [
                                                user.location.ll[0],
                                                user.location.ll[1]
                                            ];
                                            return (_jsx(React.Fragment, { children: _jsx(Circle, { center: position, radius: 15000, pathOptions: {
                                                        color: user.isHost ? '#fbbf24' : '#8b5cf6',
                                                        fillColor: user.isHost ? '#fbbf24' : '#8b5cf6',
                                                        fillOpacity: 0.4,
                                                        weight: 2
                                                    }, children: _jsx(Tooltip, { permanent: true, direction: "top", offset: [0, -10], children: _jsxs("div", { style: { textAlign: 'center', fontWeight: 'bold' }, children: [user.username, user.isHost ? ' ðŸ‘‘' : ''] }) }) }) }, user.socketId));
                                        })] }) })] }), _jsxs("div", { className: "user-list-section", children: [_jsx("h2", { children: "\u00F0\u0178\u2018\u00A5 Connected Users" }), _jsx("div", { className: "user-list", children: analyticsData?.users.map(user => {
                                    // Debug logging to see what fingerprint data we're receiving
                                    console.log('ðŸ“Š UserAnalyticsPanel received user data:', {
                                        username: user.username,
                                        hasFingerprint: !!user.fingerprint,
                                        platform: user.fingerprint?.platform,
                                        userAgent: user.fingerprint?.userAgent?.substring(0, 50),
                                        webgl: user.fingerprint?.webgl,
                                        deviceMemory: user.fingerprint?.deviceMemory
                                    });
                                    const os = getOS(user.fingerprint?.platform, user.fingerprint?.userAgent);
                                    const deviceType = getDeviceType(user.fingerprint?.userAgent, user.fingerprint?.touchSupport);
                                    const gpu = getGPU(user.fingerprint?.webgl);
                                    console.log('ðŸ“Š Parsed hardware:', { os, gpu, deviceMemory: user.fingerprint?.deviceMemory });
                                    return (_jsxs("div", { className: "user-card", children: [_jsx("div", { className: "user-header", children: _jsxs("div", { className: "user-name", children: [user.username, user.isHost && _jsx("span", { className: "host-badge", children: "\u00F0\u0178\u2018\u2018" }), user.browser && _jsx("span", { className: "browser-badge", children: user.browser }), deviceType && _jsx("span", { className: "device-badge", children: deviceType })] }) }), user.location && (_jsxs("div", { className: "user-location", children: ["\u00F0\u0178\u201C\u008D ", user.location.city, ", ", user.location.region] })), _jsxs("div", { className: "user-stats", children: [_jsxs("div", { className: "stat-item", children: [_jsx("span", { className: "stat-label-text", children: "Total Time" }), _jsx("span", { className: "stat-value-text", children: user.sessionMetrics ? formatMs(user.sessionMetrics.totalTimeMs) : '0s' })] }), _jsxs("div", { className: "stat-item", children: [_jsx("span", { className: "stat-label-text", children: "Total Active" }), _jsx("span", { className: "stat-value-text", children: user.sessionMetrics ? formatMs(user.sessionMetrics.activeTimeMs) : '0s' })] }), _jsxs("div", { className: "stat-item", children: [_jsx("span", { className: "stat-label-text", children: "Session Time" }), _jsx("span", { className: "stat-value-text", children: formatDuration(user.joinedAt) })] }), _jsxs("div", { className: "stat-item", children: [_jsx("span", { className: "stat-label-text", children: "Latency" }), _jsxs("span", { className: "stat-value-text", children: [user.connectionStats?.latency || 0, "ms"] })] }), _jsxs("div", { className: "stat-item", children: [_jsx("span", { className: "stat-label-text", children: "Packet Loss" }), _jsxs("span", { className: "stat-value-text", children: [user.connectionStats ? user.connectionStats.packetLoss.toFixed(1) : '0.0', "%"] })] }), _jsxs("div", { className: "stat-item", children: [_jsx("span", { className: "stat-label-text", children: "Bitrate" }), _jsxs("span", { className: "stat-value-text", children: [user.connectionStats ? (user.connectionStats.bitrate / 1000000).toFixed(1) : '0.0', " Mbps"] })] }), _jsxs("div", { className: "stat-item", children: [_jsx("span", { className: "stat-label-text", children: "FPS" }), _jsx("span", { className: "stat-value-text", children: user.connectionStats?.frameRate || 'N/A' })] }), _jsxs("div", { className: "stat-item", children: [_jsx("span", { className: "stat-label-text", children: "Resolution" }), _jsx("span", { className: "stat-value-text", children: user.connectionStats?.resolution || 'N/A' })] }), _jsxs("div", { className: "stat-item", children: [_jsx("span", { className: "stat-label-text", children: "Network Speed" }), _jsx("span", { className: "stat-value-text", children: user.sessionMetrics?.networkType || user.connectionStats?.networkType || 'N/A' })] }), _jsxs("div", { className: "stat-item", children: [_jsx("span", { className: "stat-label-text", children: "Messages" }), _jsx("span", { className: "stat-value-text", children: user.sessionMetrics?.messagesCount || 0 })] }), _jsxs("div", { className: "stat-item", children: [_jsx("span", { className: "stat-label-text", children: "Webcam On" }), _jsx("span", { className: "stat-value-text", children: user.sessionMetrics ? formatMs(user.sessionMetrics.webcamOnTimeMs) : '0s' })] }), _jsxs("div", { className: "stat-item", children: [_jsx("span", { className: "stat-label-text", children: "Data (from cam)" }), _jsx("span", { className: "stat-value-text", children: formatBytes(user.connectionStats?.bytesTransferred) })] }), _jsxs("div", { className: "stat-item", children: [_jsx("span", { className: "stat-label-text", children: "OS" }), _jsx("span", { className: "stat-value-text", children: os })] }), _jsxs("div", { className: "stat-item", children: [_jsx("span", { className: "stat-label-text", children: "Device Type" }), _jsx("span", { className: "stat-value-text", children: deviceType })] }), _jsxs("div", { className: "stat-item", children: [_jsx("span", { className: "stat-label-text", children: "CPU Cores" }), _jsxs("span", { className: "stat-value-text", children: [user.fingerprint?.hardwareConcurrency || 'N/A', " ", user.fingerprint?.hardwareConcurrency ? 'cores' : ''] })] }), _jsxs("div", { className: "stat-item", children: [_jsx("span", { className: "stat-label-text", children: "RAM" }), _jsx("span", { className: "stat-value-text", children: user.fingerprint?.deviceMemory ? `${user.fingerprint.deviceMemory} GB` : 'N/A' })] }), _jsxs("div", { className: "stat-item", children: [_jsx("span", { className: "stat-label-text", children: "GPU" }), _jsx("span", { className: "stat-value-text", children: gpu })] }), user.sessionMetrics?.batteryLevel !== null && user.sessionMetrics?.batteryLevel !== undefined ? (_jsxs("div", { className: "stat-item", children: [_jsx("span", { className: "stat-label-text", children: "Battery" }), _jsxs("span", { className: "stat-value-text", children: [user.sessionMetrics.batteryLevel, "%", user.sessionMetrics.isCharging ? ' âš¡' : ''] })] })) : (_jsxs("div", { className: "stat-item", children: [_jsx("span", { className: "stat-label-text", children: "Fingerprint" }), _jsx("span", { className: "stat-value-text", children: shortenFingerprint(user.fingerprint?.fingerprintId) })] }))] })] }, user.socketId));
                                }) })] })] })] }));
}
